import { sortTableData } from "@dotlegal/dotlegal-ui-components";

type SortOrder = "asc" | "desc";
export type SortOverrides<T> = Record<string, (a: T, b: T, order: SortOrder) => number>;

// Replicates DotLegalTable's client-side search (its filterTableData): a case-insensitive substring match across
// every row value, where a "quoted" term means exact-match. Needed by pages that filter client-side and export
// the on-screen rows, since the table narrows the rows internally and never hands the result back. excludedKeys
// mirrors the table's columnsExcludedFromFilter, so pass the same keys to both.
export function filterRowsBySearch<T extends object>(rows: Array<T>, searchString: string, excludedKeys: Array<keyof T> = []): Array<T> {
    if (!searchString) return rows;

    let search = searchString;
    const firstQuote = search.indexOf('"');
    const lastQuote = search.lastIndexOf('"');
    let exact = "";
    if (firstQuote > -1 && lastQuote > -1 && firstQuote !== lastQuote) exact = search.substring(firstQuote + 1, lastQuote);
    if (firstQuote === lastQuote) search = search.slice(firstQuote + 1);

    const lowerSearch = search.toLowerCase();
    const lowerExact = exact.toLowerCase();

    return rows.filter((row) =>
        Object.entries(row).some(([key, value]) => {
            if (value === null || value === undefined || excludedKeys.includes(key as keyof T)) return false;
            const text = String(value).toLowerCase();
            return exact === "" ? text.indexOf(lowerSearch) > -1 : text === lowerExact;
        })
    );
}

// A note is HTML, so searching it would match its markup ("strong", "p"). Tables exclude it here and let search reach
// the note through a plain-text row field (notePlainText) instead.
export const noteExcludedFromSearch: Array<"note"> = ["note"];

// The rows exactly as a client-side DotLegalTable shows them: narrowed by the search box and ordered by the current
// sort. Used to build exports so the file mirrors the on-screen table. Columns whose display value differs from
// their sort value pass a comparator through sortOverrides, keyed by the table header's `property`; everything else
// defers to the library's own sortTableData, the exact function the table runs.
export function getDisplayedRows<T extends object>(
    rows: Array<T>,
    searchString: string,
    sortBy: string,
    sortOrder: SortOrder,
    sortOverrides?: SortOverrides<T>,
    excludedKeys?: Array<keyof T>
): Array<T> {
    const filtered = [...filterRowsBySearch(rows, searchString, excludedKeys)];
    const override = sortOverrides?.[sortBy];

    return override ? filtered.sort((a, b) => override(a, b, sortOrder)) : sortTableData(filtered, sortBy as keyof T, sortOrder);
}
